// Theme foundation shared by the Time Off List (green accent) and the
// Schedule Time Off batch-create (orange accent) screens.
//
// One accent hex drives every accent element. All derivations live here so a
// single token change re-colors the whole screen (no hardcoded accent hex in
// the components). The fixed Ontiloo neutral palette (navy/grays/danger/warning)
// is exposed as CSS custom properties scoped to each modal wrapper.

// Matches the app's action blue (Quick Booking header rule, Check In button) so
// the modal reads as part of the app chrome; its #E8F3FF tint is the header band.
export const TIME_OFF_ACCENT = '#148CF1'; // Time Off List screen accent (blue)
// Both time-off screens share one accent so the flow reads as a single feature.
export const SCHEDULE_ACCENT = TIME_OFF_ACCENT; // Schedule Time Off batch screen accent
export const NAVY = '#001848'; // NOT themed: All-day pill, per-staff count badge, navy buttons

type RGB = { r: number; g: number; b: number };

const toRgb = (hex: string): RGB => {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

/** Darken each RGB channel by `1 - ratio` (default 14% => ×0.86). */
export const deriveAccentHover = (hex: string, ratio = 0.14): string => {
  const { r, g, b } = toRgb(hex);
  const d = (x: number) => Math.round(x * (1 - ratio));
  return `rgb(${d(r)},${d(g)},${d(b)})`;
};

/** Mix `ratio` of the way toward white per channel: c + (255 - c) * ratio. */
export const mixTowardWhite = (hex: string, ratio: number): string => {
  const { r, g, b } = toRgb(hex);
  const m = (x: number) => Math.round(x + (255 - x) * ratio);
  return `rgb(${m(r)},${m(g)},${m(b)})`;
};

const rgba = (hex: string, alpha: number): string => {
  const { r, g, b } = toRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
};

export interface AccentTheme {
  acc: string;
  accHover: string;
  accSoft: string; // timed pill / in-range day fill
  accSofter: string; // selected reason chip bg
  accSoft50: string; // range banner bg
  accSoft200: string; // range banner border
  accDisabled: string; // disabled Create button
}

/** Build the full accent derivation set for a given accent hex. */
export const makeAccentTheme = (hex: string): AccentTheme => ({
  acc: hex,
  accHover: deriveAccentHover(hex),
  accSoft: mixTowardWhite(hex, 0.84),
  accSofter: mixTowardWhite(hex, 0.93),
  accSoft50: mixTowardWhite(hex, 0.9),
  accSoft200: mixTowardWhite(hex, 0.74),
  accDisabled: mixTowardWhite(hex, 0.72),
});

/**
 * Emit the CSS custom-property block for a modal wrapper. Fixed Ontiloo neutral
 * tokens + the accent-derived tokens for the passed accent. Inner styled
 * components reference these via `var(--acc)`, `var(--fg-1)`, etc.
 */
export const buildScopeVars = (accentHex: string): string => {
  const t = makeAccentTheme(accentHex);
  return `
    --acc: ${t.acc};
    --acc-hover: ${t.accHover};
    --acc-soft: ${t.accSoft};
    --acc-softer: ${t.accSofter};
    --acc-soft50: ${t.accSoft50};
    --acc-soft200: ${t.accSoft200};
    --acc-disabled: ${t.accDisabled};

    --brand-navy: ${NAVY};
    --fg-1: #001848;
    --fg-2: #2C334B;
    --fg-3: #6B7280;
    --fg-4: #9AA3B2;

    --bg-app: #F7F8FB;
    --bg-surface: #FFFFFF;
    --bg-subtle: #F2F4F8;
    --bg-row-hover: #F7F9FC;

    --border-1: #E5E8EF;
    --border-2: #D6DBE3;
    --border-focus: ${accentHex};
    --ring-focus: 0 0 0 3px ${rgba(accentHex, 0.18)};

    --danger: #DC2348;
    --danger-hover: #BA1A38;
    --danger-100: #FEDFDC;

    --warning-100: #FEEFC8;

    --r-xs: 4px;
    --r-sm: 6px;
    --r-md: 10px;
    --r-lg: 14px;
    --r-xl: 18px;
    --r-pill: 999px;

    --shadow-sm: 0 2px 6px rgba(0,24,72,0.06);
    --shadow-md: 0 6px 18px rgba(0,24,72,0.08);
    --shadow-lg: 0 16px 40px rgba(0,24,72,0.12);

    --font-time-off: 'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-family: var(--font-time-off);
  `;
};
